import { useState, useCallback } from 'react';

const useChatBot = () => {
  const [messages, setMessages] = useState([
    {
      text: `System initialized...\nInstalling personality drivers...\nUpdating memory cores...\nDigital persona active.`,
      sender: 'bot'
    }
  ]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentActions, setCurrentActions] = useState([
    { label: 'Ellenox', type: 'link', url: 'https://ellenox.com' },
    { label: 'LinkedIn', type: 'link', url: 'https://www.linkedin.com/in/just-ankur-bagchi' },
    { label: 'X', type: 'link', url: 'https://x.com/justankurbagchi' },
    { label: 'Discord', type: 'link', url: 'https://discord.com/users/USERID202251384236670977' },
    { label: 'Instagram', type: 'link', url: 'https://www.instagram.com/ankur.bagchi/' },
    { label: 'Email', type: 'link', url: 'mailto:ankur@ellenox.com/' }
  ]);
  const [sendButtonDisabled, setSendButtonDisabled] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [inputPlaceholder, setInputPlaceholder] = useState("Type your message...");
  const [userTag, setUserTag] = useState('guest');

  // Resolve API base depending on environment
  const getApiBaseUrl = () => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isLocal ? 'http://localhost:3000/api' : 'https://ankurbagchi.up.railway.app/api';
  };

  const addMessage = useCallback((text, sender) => {
    setMessages(prev => [...prev, { text, sender }]);
  }, []);

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const addMessageChunks = useCallback(async (chunks) => {
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      
      // Add a small typing delay before each chunk (except the first one)
      if (i > 0) {
        setIsTyping(true);
        await delay(800 + Math.random() * 600); // 800-1400ms delay
        setIsTyping(false);
      }
      
      // Add the message chunk
      addMessage(chunk, 'bot');
      
      // Small pause between chunks for natural flow
      if (i < chunks.length - 1) {
        await delay(200 + Math.random() * 300); // 200-500ms pause
      }
    }
  }, [addMessage]);

  const disableSendButton = useCallback(() => {
    setSendButtonDisabled(true);
    setInputDisabled(true);
    setInputPlaceholder('Chat disabled - use "Restart convo" to continue');
  }, []);

  const getDefaultActions = () => [
    { label: 'Ellenox', type: 'link', url: 'https://ellenox.com' },
    { label: 'LinkedIn', type: 'link', url: 'https://www.linkedin.com/in/just-ankur-bagchi' },
    { label: 'X', type: 'link', url: 'https://x.com/justankurbagchi' }
  ];

  const sendMessage = useCallback(async (messageText) => {
    if (!messageText || isTyping) {
      return;
    }

    // Clear input and disable sending
    setIsTyping(true);
    setSendButtonDisabled(true);

    // Add user message to chat
    addMessage(messageText, 'user');
    
    // Add to conversation history
    const newHistory = [...conversationHistory, { role: 'user', content: messageText }];
    setConversationHistory(newHistory);

    try {
      // Set up timeout for the request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 45000); // 45 second timeout

      const requestBody = {
        message: messageText,
        conversationHistory: newHistory
      };

      const response = await fetch(`${getApiBaseUrl()}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Check if this is an error response from server
      const isServerError = data.error || (data.actions && data.actions.some(action => action.type === 'restart'));
      
      // Handle chunked messages or fallback to single message
      if (data.messageChunks && Array.isArray(data.messageChunks) && data.messageChunks.length > 0) {
        await addMessageChunks(data.messageChunks);
      } else {
        // Fallback to legacy single message
        addMessage(data.response, 'bot');
      }
      
      // Handle actions - only update if AI provided actions, otherwise keep previous
      if (data.actions !== null && data.actions !== undefined) {
        setCurrentActions(data.actions || []);
        
        // If server sent an error with restart action, disable send button
        if (isServerError) {
          disableSendButton();
        }
      }

      // Handle nickname update if provided by server
      if (data.nickname && typeof data.nickname === 'string' && data.nickname.trim().length > 0) {
        setUserTag(data.nickname.trim());
      }

      // Add to conversation history (use combined message for history)
      const fullMessage = data.messageChunks ? data.messageChunks.join(' ') : data.response;
      setConversationHistory(prev => [...prev, { role: 'assistant', content: fullMessage }]);

    } catch (error) {
      // Determine error type and show appropriate message
      let errorMessage;
      if (error.name === 'AbortError') {
        errorMessage = 'Response took too long - the AI might be overloaded. Try again?';
      } else if (error.message && error.message.includes('Failed to fetch')) {
        errorMessage = 'Connection failed. Check your internet and try again.';
      } else if (error.message && error.message.includes('HTTP error')) {
        errorMessage = 'Server error occurred. The AI service might be temporarily down.';
      } else {
        errorMessage = 'Sorry, I\'m having trouble connecting right now. Please try again later.';
      }
      
      addMessage(errorMessage, 'bot');
      
      // Set default actions on error and update current actions
      const defaultActions = getDefaultActions();
      const restartAction = { label: 'Restart convo', type: 'restart', url: 'reload' };
      setCurrentActions([...defaultActions, restartAction]);
      
      // Disable send button after error
      disableSendButton();
    } finally {
      setIsTyping(false);
      // Only re-enable if not permanently disabled due to error
      if (!sendButtonDisabled) {
        setSendButtonDisabled(false);
      }
    }
  }, [isTyping, conversationHistory, addMessage, addMessageChunks, disableSendButton, sendButtonDisabled]);

  return {
    messages,
    isTyping,
    currentActions,
    sendButtonDisabled,
    inputDisabled: inputDisabled || isTyping,
    inputPlaceholder,
    userTag,
    sendMessage
  };
};

export default useChatBot;
